import hotkeys from 'hotkeys-js'
import { eventChannel } from 'redux-saga'
import { all, call, cancelled, put, select, throttle } from 'redux-saga/effects'

import { isToolEnabled } from '../../hooks/useToolEnabler'
import {
    Arrow,
    Border,
    Count,
    Cutout,
    Flag,
    FloorLevelBreaklineTool,
    Highlight,
    Line,
    Measure,
    Pan,
    Pen,
    Pitch,
    RadiusLine,
    Rectangle,
    Select,
} from '../../lib/toolBoxes/2D'
import {
    getSelectedMaterial,
    selectedAiSuggestionIds,
    selectFocusedOverlayChunkId,
    selectIMUPDrawableLocations,
} from '../../slices/2D'
import { selectJoistDirection } from '../../slices/common'
import { selectDocumentMappingByActiveDocument } from '../../slices/documents'
import { selectActiveGeometryGroup } from '../../slices/geometry'
import {
    selectAreConflictsBeingReviewed,
    selectAreConflictsResolutionComplete,
    selectAreMaterialInteractionsBlocked,
    selectIsMaterialGroupActivationBlocked,
    selectIsOverlaidChunkLocked,
} from '../../slices/masterSetPlan'
import { selectProject } from '../../slices/project'
import { activateTool } from '../../slices/tools'

export const hotkeyToolMap: Record<string, { NAME: string }> = {
    v: Select,
    h: Pan,
    r: Rectangle,
    p: Pen,
    l: Line,
    'shift+r': RadiusLine,
    'shift+c': Count,
    'shift+p': Border,
    o: Cutout,
    j: Arrow,
    m: Measure,
    'shift+m': Pitch,
    'shift+h': Highlight,
    b: FloorLevelBreaklineTool,
    f: Flag,
}

export function createHotkeysChannel() {
    const allHotkeys = Object.keys(hotkeyToolMap).join(', ')

    return eventChannel((emitter) => {
        hotkeys(allHotkeys, (_, handler) => {
            emitter({ hotkey: handler.key })
        })

        return () => {
            hotkeys.unbind('*')
        }
    })
}

export function* handleKeyEvents({ hotkey }: { hotkey: string }) {
    const params = yield all({
        activeDrawableGroup: select(selectActiveGeometryGroup),
        drawableLocations: select(selectIMUPDrawableLocations),
        selectedMatType: select(getSelectedMaterial),
        joistDirection: select(selectJoistDirection),
        activeDocumentMapping: select(selectDocumentMappingByActiveDocument),
        project: select(selectProject),
        overlayChunkId: select(selectFocusedOverlayChunkId),
        isOverlaidChunkLocked: select(selectIsOverlaidChunkLocked),
        selectedAiSuggestions: select(selectedAiSuggestionIds),
        areConflictsBeingReviewed: select(selectAreConflictsBeingReviewed),
        isConflictResolutionComplete: select(selectAreConflictsResolutionComplete),
        areMaterialInteractionsBlocked: select(selectAreMaterialInteractionsBlocked),
        isMaterialGroupActivationBlocked: select(selectIsMaterialGroupActivationBlocked),
    })

    const tool = hotkeyToolMap[hotkey]

    if (!tool) return

    const isEnabled = yield call(isToolEnabled, tool.NAME, params)

    if (isEnabled) {
        yield put({ type: activateTool.type, payload: tool.NAME })
    }
}

export function* watchKeyboardEvents() {
    const keyboardChannel = yield call(createHotkeysChannel)

    try {
        yield throttle(200, keyboardChannel, handleKeyEvents)
    } finally {
        if (yield cancelled()) {
            keyboardChannel.close()
        }
    }
}
